import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { TenantLayout, useTenant, RequireWrite } from "@/components/tenant/tenant-layout";
import { createDocument, uploadDocumentFile, type DocumentType } from "@/lib/api/documents";
import { listFunds } from "@/lib/api/funds";
import { listLps } from "@/lib/api/lps";
import { parseFile } from "@/lib/files/parse-file";
import { useAuth } from "@/lib/auth/auth-provider";

const TYPE_OPTIONS: { value: DocumentType; label: string }[] = [
  { value: "side_letter", label: "Side letter" },
  { value: "lpa", label: "LPA" },
  { value: "sub_doc", label: "Subscription document" },
  { value: "amendment", label: "Amendment" },
  { value: "other", label: "Other" },
];

function UploadInner() {
  const tenant = useTenant();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState("");
  const [fundId, setFundId] = useState("");
  const [lpId, setLpId] = useState("");
  const [docType, setDocType] = useState<DocumentType>("side_letter");
  const [executedDate, setExecutedDate] = useState("");
  const [phase, setPhase] = useState<string | null>(null);

  const { data: funds } = useQuery({
    queryKey: ["funds", tenant.id],
    queryFn: () => listFunds(tenant.id),
  });
  const { data: lps } = useQuery({
    queryKey: ["lps", tenant.id],
    queryFn: () => listLps(tenant.id),
  });

  const upload = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error("Choose a file first.");
      if (!fundId) throw new Error("Choose the fund this document belongs to.");

      setPhase("Parsing file…");
      const parsed = await parseFile(file);

      setPhase("Uploading to encrypted storage…");
      const { storagePath } = await uploadDocumentFile(tenant.id, file);

      setPhase("Registering document…");
      const doc = await createDocument({
        tenant_id: tenant.id,
        fund_id: fundId,
        lp_id: lpId || null,
        document_type: docType,
        title: title.trim() || file.name.replace(/\.(pdf|txt)$/i, ""),
        storage_path: storagePath,
        file_hash: parsed.sha256,
        page_count: parsed.pageCount,
        word_count: parsed.wordCount,
        extracted_text: parsed.text,
        executed_date: executedDate || null,
        uploaded_by: user?.id ?? null,
      });
      return doc;
    },
    onSuccess: (doc) => {
      void navigate({
        to: "/t/$slug/documents/$documentId",
        params: { slug: tenant.slug, documentId: doc.id },
      });
    },
    onSettled: () => setPhase(null),
  });

  return (
    <RequireWrite>
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Upload document</h1>
        <p className="text-sm text-neutral-400">
          PDF or plain text. The file is hashed, parsed in your browser, and stored encrypted —
          extraction runs on the next screen.
        </p>
      </header>

      <form
        className="max-w-xl space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          upload.mutate();
        }}
      >
        <div className="space-y-1">
          <label htmlFor="file" className="block text-xs font-medium text-neutral-300">
            File (PDF or .txt)
          </label>
          <input
            id="file"
            type="file"
            accept=".pdf,.txt,application/pdf,text/plain"
            onChange={(e) => {
              const f = e.target.files?.[0] ?? null;
              setFile(f);
              if (f && !title) setTitle(f.name.replace(/\.(pdf|txt)$/i, ""));
            }}
            className="block w-full rounded border border-neutral-800 bg-neutral-900 px-3 py-2 text-sm text-neutral-200 file:mr-3 file:rounded file:border-0 file:bg-neutral-700 file:px-2 file:py-1 file:text-xs file:text-neutral-100"
          />
        </div>

        <div className="space-y-1">
          <label htmlFor="title" className="block text-xs font-medium text-neutral-300">
            Title
          </label>
          <input
            id="title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Northcliff Pension Trust — Side Letter"
            className="block w-full rounded border border-neutral-800 bg-neutral-900 px-3 py-2 text-sm text-neutral-200 placeholder:text-neutral-600"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <label htmlFor="fund" className="block text-xs font-medium text-neutral-300">
              Fund <span className="text-red-400">*</span>
            </label>
            <select
              id="fund"
              value={fundId}
              onChange={(e) => setFundId(e.target.value)}
              required
              className="block w-full rounded border border-neutral-800 bg-neutral-900 px-3 py-2 text-sm text-neutral-200"
            >
              <option value="">Select fund…</option>
              {funds?.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <label htmlFor="lp" className="block text-xs font-medium text-neutral-300">
              LP (empty for fund-level docs)
            </label>
            <select
              id="lp"
              value={lpId}
              onChange={(e) => setLpId(e.target.value)}
              className="block w-full rounded border border-neutral-800 bg-neutral-900 px-3 py-2 text-sm text-neutral-200"
            >
              <option value="">No specific LP</option>
              {lps?.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.legal_name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <label htmlFor="doc-type" className="block text-xs font-medium text-neutral-300">
              Document type
            </label>
            <select
              id="doc-type"
              value={docType}
              onChange={(e) => setDocType(e.target.value as DocumentType)}
              className="block w-full rounded border border-neutral-800 bg-neutral-900 px-3 py-2 text-sm text-neutral-200"
            >
              {TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <label htmlFor="executed" className="block text-xs font-medium text-neutral-300">
              Executed date
            </label>
            <input
              id="executed"
              type="date"
              value={executedDate}
              onChange={(e) => setExecutedDate(e.target.value)}
              className="block w-full rounded border border-neutral-800 bg-neutral-900 px-3 py-2 text-sm text-neutral-200"
            />
          </div>
        </div>

        {upload.error ? (
          <div className="rounded border border-red-900/60 bg-red-950/30 px-3 py-2 text-xs text-red-300">
            {upload.error.message}
          </div>
        ) : null}

        <button
          type="submit"
          disabled={upload.isPending || !file || !fundId}
          className="rounded bg-neutral-100 px-4 py-2 text-sm font-medium text-neutral-950 hover:bg-white disabled:cursor-not-allowed disabled:opacity-50"
        >
          {upload.isPending ? (phase ?? "Uploading…") : "Upload"}
        </button>
      </form>
    </RequireWrite>
  );
}

export function DocumentsUploadPage() {
  return (
    <TenantLayout>
      <UploadInner />
    </TenantLayout>
  );
}
